import { Link } from "react-router-dom";
import type { Film, Topic } from "@/types/content";
import { formatViews } from "@/utils/format";

interface HomeSidebarProps {
  topics: Topic[];
  popular: Film[];
}

export default function HomeSidebar({ topics, popular }: HomeSidebarProps) {
  return (
    <aside className="w-full lg:w-80 shrink-0 space-y-6">
      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-4 rounded-full bg-accent-500"></span>
          <h3 className="font-heading font-bold text-sm text-foreground-950">专题导航</h3>
        </div>
        <ul className="space-y-1">
          {topics.map((topic) => (
            <li key={topic.id}>
              <Link
                to="/topic"
                className="group flex items-start gap-3 p-2.5 rounded-md hover:bg-background-100 transition-colors"
              >
                <span className="w-8 h-8 shrink-0 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-sm"></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {topic.title}
                  </span>
                  <span className="block text-[11px] text-foreground-400 mt-0.5 line-clamp-1">
                    {topic.subtitle}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-4 rounded-full bg-accent-500"></span>
          <h3 className="font-heading font-bold text-sm text-foreground-950">热门排行</h3>
        </div>
        <ol className="space-y-1">
          {popular.map((film, i) => (
            <li key={film.id}>
              <Link
                to={`/detail/${film.id}`}
                className="group flex items-center gap-3 py-2"
              >
                <span
                  className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                    i < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                    {film.title}
                  </span>
                  <span className="block text-[11px] text-foreground-400 mt-0.5">
                    {film.type} · {formatViews(film.views)} 次浏览
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section className="relative rounded-lg overflow-hidden border border-background-200">
        <div className="relative w-full h-40">
          <img
            src="https://readdy.ai/api/search-image?query=minimal%20dark%20hospital%20corridor%20with%20a%20single%20glowing%20ceiling%20light%2C%20deep%20blue%20tones%2C%20abstract%20cinematic%20atmosphere%2C%20no%20text&width=800&height=480&seq=nightward-ad-01&orientation=landscape"
            alt="夜勤病栋专题推广位"
            title="夜勤病栋影视专题推广"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 to-primary-950/20"></div>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-4">
          <p className="text-[11px] text-accent-300 tracking-wide">推广 · 专题精选</p>
          <p className="text-sm text-background-50 font-medium mt-1 leading-snug">
            深度专题：深夜医院里的十段故事
          </p>
        </div>
      </section>
    </aside>
  );
}